import React, { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChantiersSidebarCollapsedProps {
  isVisible: boolean;
  onExpand: () => void;
  totalChantiers: number;
  chantiersEnCours: number;
}

/**
 * Onglet plié de la sidebar Chantiers (90px × 280px)
 * Affiche : icône + badge, "CHANTIERS" vertical, compteurs
 */
const ChantiersSidebarCollapsed = ({
  isVisible,
  onExpand,
  totalChantiers,
  chantiersEnCours,
}: ChantiersSidebarCollapsedProps) => {
  const [shouldBounce, setShouldBounce] = useState(false);

  // Animation bounce à l'arrivée sur la page
  useEffect(() => {
    if (isVisible) {
      const startTimer = setTimeout(() => setShouldBounce(true), 600);
      const stopTimer = setTimeout(() => setShouldBounce(false), 2100);
      return () => {
        clearTimeout(startTimer);
        clearTimeout(stopTimer);
      };
    }
  }, []);

  if (!isVisible) return null;

  return (
    <button
      onClick={onExpand}
      className={cn(
        // Position fixe bord droit, centré verticalement
        'fixed right-0 top-1/2 -translate-y-1/2 z-50',
        // Dimensions
        'w-[90px] min-h-[280px]',
        // Style visuel
        'bg-card/95 backdrop-blur-md',
        'border border-r-0 border-border/50 rounded-l-2xl',
        'shadow-2xl shadow-black/30',
        // Contenu centré en colonne
        'flex flex-col items-center py-6 gap-4',
        // Hover effect
        'hover:w-[100px] hover:bg-card transition-all duration-200',
        'group cursor-pointer',
        // Animation bounce à l'arrivée
        shouldBounce && 'animate-subtle-bounce'
      )}
    >
      {/* ═══ ICÔNE BUILDING + BADGE ═══ */}
      <div className="relative">
        <div className="p-3 bg-orange-500/10 rounded-xl group-hover:bg-orange-500/20 transition-colors">
          <Building2 className="w-7 h-7 text-orange-500 group-hover:scale-110 transition-transform" />
        </div>
        {chantiersEnCours > 0 && (
          <span className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
            {chantiersEnCours > 9 ? '9+' : chantiersEnCours}
          </span>
        )}
      </div>

      {/* ═══ TEXTE "CHANTIERS" VERTICAL ═══ */}
      <div
        className="text-sm font-bold tracking-widest text-muted-foreground group-hover:text-foreground transition-colors"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        CHANTIERS
      </div>

      {/* ═══ SÉPARATEUR ═══ */}
      <div className="w-10 h-px bg-border/50" />

      {/* ═══ COMPTEUR TOTAL ═══ */}
      <div className="flex flex-col items-center">
        <span className="text-3xl font-black text-foreground leading-none">
          {totalChantiers}
        </span>
        <span className="text-xs font-medium text-muted-foreground mt-1">
          TOTAL
        </span>
      </div>

      {/* ═══ EN COURS ═══ */}
      {chantiersEnCours > 0 && (
        <>
          <div className="w-10 h-px bg-border/50" />
          <div className="flex flex-col items-center">
            <span className="text-lg font-bold text-orange-500">
              {chantiersEnCours}
            </span>
            <span className="text-[10px] font-medium text-muted-foreground/70">
              EN COURS
            </span>
          </div>
        </>
      )}
    </button>
  );
};

export default ChantiersSidebarCollapsed;
